import { AnalyticsContext, AnalyticsEvent, AnalyticsEventNameLike, AnalyticsEventProperties, AnalyticsInitOptions, AnalyticsPropertyKey } from "./analytics_types.js";
import { getUserLevel } from "./analytics_identity.js";
import { AnalyticsTransport } from "./transports/analytics_transport.js";
import { ConsoleAnalyticsMethod, ConsoleAnalyticsTransport } from "./transports/console_analytics_transport.js";
import { AnalyticStore } from "./analytic_store.js";
import { crashCapture } from "../crash/crash_reporter.js";
import { levelContentProperties } from "../level_pack/level_content_identity.js";
import { AuthSessionStore } from "../api/auth_session.js";

const safeString = ( v: unknown ): string =>
{
  try
  {
    return String( v );
  } catch
  {
    return '';
  }
};

const makeSessionId = (): string =>
{
  try
  {
    const any_crypto = ( globalThis as { crypto?: Crypto } ).crypto;
    const uuid = any_crypto?.randomUUID?.();
    if ( uuid ) return uuid;
  } catch {}

  return `${Date.now()}_${Math.random().toString( 16 ).slice( 2 )}`;
};

export class AnalyticsService
{
  private transport: AnalyticsTransport;
  private enabled = true;

  private context: AnalyticsContext = {};
  private common_properties: AnalyticsEventProperties = {};

  private session_id: string = makeSessionId();
  private user_id?: string;
  private seq = 0;

  constructor( transport: AnalyticsTransport = new ConsoleAnalyticsTransport( ConsoleAnalyticsMethod.INFO ) )
  {
    this.transport = transport;
  }

  init( options: AnalyticsInitOptions = {} ): void
  {
    if ( typeof options.enabled === 'boolean' ) this.enabled = options.enabled;
    if ( options.session_id ) this.session_id = options.session_id;
    if ( options.user_id ) this.user_id = options.user_id;
    if ( options.context ) this.context = { ...this.context, ...options.context };
    if ( options.common_properties ) this.common_properties = { ...this.common_properties, ...options.common_properties };
  }

  setEnabled( enabled: boolean ): void { this.enabled = enabled; }
  setUserId( user_id?: string ): void { this.user_id = user_id; }
  setSessionId( session_id: string ): void { this.session_id = session_id; }
  setContext( ctx: Partial<AnalyticsContext> ): void { this.context = { ...this.context, ...ctx }; }
  setCommonProperties( props: AnalyticsEventProperties ): void { this.common_properties = { ...this.common_properties, ...props }; }
  setTransport( transport: AnalyticsTransport ): void { this.transport = transport; }
  getTransport(): AnalyticsTransport { return this.transport; }

  getSessionId(): string { return this.session_id; }
  getUserId(): string | undefined { return this.user_id; }

  /**
   * Common properties, then the level content the player is on (release id,
   * revision, collection version — on every event), the player's current
   * level and server player id read at send time, then the event's own.
   */
  private eventProperties( properties?: AnalyticsEventProperties ): AnalyticsEventProperties | undefined
  {
    const player_id = AuthSessionStore.getPlayerId();
    const merged = {
      ...levelContentProperties(),
      ...this.common_properties,
      [ AnalyticsPropertyKey.USERLEVEL ]: getUserLevel(),
      ...( player_id !== null && Number.isFinite( player_id ) ? { [ AnalyticsPropertyKey.CUSTOMERUSERID ]: player_id } : {} ),
      ...( properties ?? {} ),
    };
    return Object.keys( merged ).length ? merged : undefined;
  }

  track( name: AnalyticsEventNameLike, properties?: AnalyticsEventProperties ): void
  {
    if ( !this.enabled ) return;

    const event_data: AnalyticsEvent = {
      name,
      ts: Date.now(),
      seq: ++this.seq,
      session_id: AnalyticStore.getSessionId(),
      session_id_serv: AnalyticStore.getSessionIdFromServer(),
      user_id: AnalyticStore.getUserId(),
      context: { ...this.context },
      properties: this.eventProperties( properties )
    };

    void this.transport.send( event_data ).catch( ( err ) =>
    {
      try
      {
        console.warn( '[ANALYTICS] transport failed', safeString( err ) );
      } catch {}

      try
      {
        crashCapture( err, { component: 'analytics', event: String( name ) } );
      } catch {}
    } );
  }
}
